import { Component, inject, OnInit } from '@angular/core';
import { Storage } from '@core/utils/storage';
import { User } from '@core/models';
import { RouterLink } from '@angular/router';
import { ReviewPermissionService } from '@shared/services/review-permission.service';
import { AuthService } from '@core/services/auth/auth.service';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-menu',
  templateUrl: './app.menu.component.html',
  standalone: false,
})
export class AppMenuComponent implements OnInit {
  private reviewPermissionService = inject(ReviewPermissionService);
  private authService = inject(AuthService);

  model: MenuItem[] = [];

  // Comprehensive mapping between menu items and permitted module names
  private readonly menuModuleMapping: { [key: string]: string } = {
    // Report submenu
    'Property Report': 'property_report',
    'Benchmarking Scorecard': 'scorecard',
    'Report Scheduler': 'report_scheduler',
    'Data Ingestion': 'data_ingestion',

    // Brand.com submenu
    'Channel Mix': 'channel_mix',
    'Visits & Revenue': 'visits_and_revenue',
    'Source Traffic': 'source_traffic',
    'Top Ref Domains': 'top_ref_domains',

    // Paid Media submenu
    Overview: 'paid_media_overview',
    'Portfolio Roll-Up': 'portfolio_rollup',
    Metasearch: 'metasearch',
    'Sponsored Listing': 'sponsored_listing',
    'Search Ads': 'search_ads',
    'Social Media Ads': 'social_media_ads',
    'OTA Sponsored Listing': 'ota_sponsored_listing',
    'Display Ads': 'display_ads',

    // Social submenu
    Facebook: 'social_facebook',

    // Reviews submenu - each has independent permission
    Dashboard: 'reviews_dashboard',
    Responses: 'reviews_responses',
    Sentiment: 'reviews_sentiment',
    'Review Feed': 'review_feed',

    // AI submenu
    'AI Report': 'ai_report',

    // User Management submenu - requires 'user' parent module
    'User List': 'user',
    'User Create': 'user',
    Roles: 'role',
    Regions: 'admin_region',
    'Ownership Groups': 'admin_ownership_group',
  };

  constructor() {}

  ngOnInit() {
    this.model = this.buildMenu();
  }

  // Helper method to rebuild menu - useful for testing after localStorage changes
  rebuildMenu() {
    // Force re-read from localStorage by refreshing the auth service
    this.authService.refreshUserFromStorage();
    this.model = this.buildMenu();
  }

  private buildMenu() {
    // Use single menu structure, mark locked items based on permitted modules
    const menuWithPermissions = this.markLockedMenuItems(ALL_MENUS);

    return menuWithPermissions;
  }

  private markLockedMenuItems(menus: MenuItem[]): MenuItem[] {
    const user = this.authService.user;

    // If user is superuser, show all menus without locks
    if (!user || user.is_superuser) {
      return menus;
    }

    const permittedModules = user.permitted_modules || [];

    // If no permitted_modules, lock everything
    const hasNoPermissions = permittedModules.length === 0;

    return menus.map((menu) => ({
      ...menu,
      items: menu.items?.map((item) =>
        this.processMenuItem(item, permittedModules, hasNoPermissions)
      ),
    }));
  }

  private processMenuItem(
    item: MenuItem,
    permittedModules: string[],
    hasNoPermissions: boolean,
    parentLabel?: string
  ): MenuItem {
    const moduleKey = this.menuModuleMapping[item.label || ''];
    // const parentModuleKey = parentLabel ? this.parentModuleMapping[parentLabel] : undefined;

    // Check if this item has children
    if (item.items && item.items.length > 0) {
      // Process children recursively, passing current label as parent
      const processedChildren = item.items.map((child) =>
        this.processMenuItem(child, permittedModules, hasNoPermissions, item.label)
      );

      // Check if parent menu should be locked (if all children are locked)
      const allChildrenLocked = processedChildren.every((child: any) => child.locked === true);

      return {
        ...item,
        items: processedChildren,
        locked: allChildrenLocked,
      };
    }

    // For leaf items (actual menu items with routes)
    if (moduleKey) {
      // Scenario 2 & 3: Strict submodule-level permission (No parent override)
      // Item is locked if:
      // 1. User has no permissions, OR
      // 2. The specific module key is not in permitted_modules
      let hasModulePermission = permittedModules.includes(moduleKey);

      // Special case: report_scheduler is merged with property_report in backend
      // If property_report is permitted, report_scheduler should also be unlocked
      if (moduleKey === 'report_scheduler' && !hasModulePermission) {
        // Check if property_report is permitted
        hasModulePermission = permittedModules.includes('property_report');
      }

      const isLocked = hasNoPermissions || !hasModulePermission;

      const processedItem: any = {
        ...item,
        locked: isLocked,
        // Add lock icon on the RIGHT side by adding a suffix icon (for parent menus)
        suffixIcon: isLocked ? 'pi pi-lock' : undefined,
        routerLink: isLocked ? undefined : item.routerLink, // Prevent navigation if locked
      };

      // Keep the original icon on the left
      // Remove custom icon if locked
      if (isLocked && processedItem.customIcon) {
        delete processedItem.customIcon;
      }

      return processedItem;
    }

    return item;
  }

  private removeReviewsMenu(menus: MenuItem[]): MenuItem[] {
    return menus.map((menu) => ({
      ...menu,
      items: menu.items?.filter((item) => item.label !== 'Reviews'),
    }));
  }
}

const ALL_MENUS = [
  {
    label: 'Analytics',
    icon: 'pi pi-fw pi-chart-pie',
    items: [
      {
        label: 'Report',
        icon: 'pi pi-fw pi-compass',
        items: [
          {
            label: 'Property Report',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/report/property'],
          },
          {
            label: 'Benchmarking Scorecard',
            icon: 'pi pi-fw pi-palette',
            routerLink: ['/report/scorecard'],
          },
          {
            label: 'Report Scheduler',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/report/scheduler'],
          },
          {
            label: 'Data Ingestion',
            icon: 'pi pi-fw pi-database',
            routerLink: ['/report/data-ingestion'],
          },
        ],
      },
      {
        label: 'Brand.com',
        icon: 'pi pi-fw pi-th-large',
        items: [
          {
            label: 'Channel Mix',
            icon: 'pi pi-fw pi-sitemap',
            routerLink: ['/brand/channel-mix'],
          },
          {
            label: 'Visits & Revenue',
            icon: 'pi pi-fw pi-chart-line',
            routerLink: ['/brand/visits-revenue'],
          },
          {
            label: 'Source Traffic',
            icon: 'pi pi-fw pi-chart-pie',
            routerLink: ['/brand/source-traffic'],
          },
          {
            label: 'Top Ref Domains',
            icon: 'pi pi-fw pi-at',
            routerLink: ['/brand/domains'],
          },
        ],
      },
      {
        label: 'Paid Media',
        icon: 'pi pi-fw pi-money-bill',
        items: [
          {
            label: 'Overview',
            icon: 'pi pi-fw pi-eye',
            routerLink: ['/media/overview'],
          },

          {
            label: 'Portfolio Roll-Up',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/media/portfolio-roll-up'],
          },
          {
            label: 'Metasearch',
            icon: 'pi pi-fw pi-search',
            routerLink: ['/media/meta-search'],
          },
          {
            label: 'Sponsored Listing',
            icon: 'pi pi-fw pi-list',
            routerLink: ['/media/sponsored'],
          },
          {
            label: 'Search Ads',
            icon: 'pi pi-fw pi-search-plus',
            routerLink: ['/media/paid-search'],
          },
          {
            label: 'Social Media Ads',
            icon: 'pi pi-fw pi-bars',
            routerLink: ['/media/paid-social'],
          },
          {
            label: 'OTA Sponsored Listing',
            icon: 'pi pi-fw pi-circle',
            routerLink: ['/media/ota'],
          },
          {
            label: 'Display Ads',
            icon: 'pi pi-fw pi-book',
            routerLink: ['/media/display-ads'],
          },
        ],
      },
      {
        label: 'Social',
        icon: 'pi pi-fw pi-globe',
        items: [
          {
            label: 'Facebook',
            icon: 'pi pi-fw pi-facebook',
            routerLink: ['/social/facebook'],
          },
        ],
      },
      {
        label: 'Reviews',
        icon: 'pi pi-fw pi-filter',
        items: [
          {
            label: 'Dashboard',
            icon: 'pi pi-fw pi-chart-bar',
            routerLink: ['/reviews/dashboard'],
          },
          {
            label: 'Responses',
            icon: 'pi pi-fw pi-comments',
            routerLink: ['/reviews/responses'],
          },
          {
            label: 'Sentiment',
            icon: 'pi pi-fw pi-heart',
            routerLink: ['/reviews/sentiment'],
          },
          {
            label: 'Review Feed',
            icon: 'pi pi-fw pi-star',
            routerLink: ['/reviews/review-feed'],
          },
        ],
      },
      {
        label: 'AI',
        customIcon: 'assets/icons/artificial-intelligence.png',
        items: [
          {
            label: 'AI Report',
            customIcon: 'assets/icons/ai-microchip.png',
            icon: 'pi pi-fw pi-cog',
            routerLink: ['/ai/ai-report'],
          },
        ],
      },
    ],
  },
  {
    label: 'User',
    icon: 'pi pi-fw pi-user',
    items: [
      {
        label: 'User Management',
        icon: 'pi pi-fw pi-user',
        items: [
          {
            label: 'User List',
            icon: 'pi pi-fw pi-users',
            routerLink: ['/users'],
          },
          {
            label: 'User Create',
            icon: 'pi pi-fw pi-user-plus',
            routerLink: ['/users/create'],
          },
          // {
          //   label: 'Roles Master',
          //   icon: 'pi pi-fw pi-key',
          //   routerLink: ['/users/roles'],
          // },
          {
            label: 'Roles',
            icon: 'pi pi-fw pi-key',
            routerLink: ['/users/roles'],
          },
          {
            label: 'Regions',
            icon: 'pi pi-fw pi-map-marker',
            routerLink: ['/users/regions'],
          },
          {
            label: 'Ownership Groups',
            icon: 'pi pi-fw pi-building',
            routerLink: ['/users/ownership-groups'],
          },
        ],
      },
    ],
  },
];
